import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ProductCard from "@/components/ProductCard";
import { Star, ShoppingCart, MessageCircle, ShieldCheck, Truck, RefreshCcw, ChevronLeft, Check, Zap } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import KlarnaBanner from "@/components/KlarnaBanner";
import WishlistButton from "@/components/WishlistButton";
import SEO, { buildBreadcrumbLd } from "@/components/SEO";
import { trackContact } from "@/lib/trackContact";

// Colour swatch map for basic Italian colour names
const COLOR_MAP = {
  nero: "#0f172a",
  bianco: "#ffffff",
  grigio: "#94a3b8",
  argento: "#cbd5e1",
  rosso: "#dc2626",
  blu: "#2563eb",
  verde: "#16a34a",
  giallo: "#eab308",
  arancione: "#f97316",
  beige: "#d6b98a",
  marrone: "#7c5a3e",
};

function pickVariantByOptions(product, chosen, effectiveOptions) {
  const variants = product.variants || [];
  const opts = effectiveOptions || [];
  if (!opts.length) return variants[0];
  // Synthetic option — value IS the variant title. This is the ONLY matching
  // strategy the storefront uses: admin variant titles are the source of truth.
  const title = chosen[opts[0].name];
  return variants.find((v) => v.title === title) || variants[0];
}

/**
 * Admin variant titles are the source of truth. Legacy Shopify `product.options`
 * are IGNORED because they frequently drift after admin edits, causing missing
 * or duplicated variants on the PDP. We build one option group whose values are
 * the variant titles; if every title is a known colour name we name the group
 * "Colore" so the UI renders color swatches, otherwise we call it "Versione".
 */
function computeEffectiveOptions(product, labels) {
  const variants = (product.variants || []).filter(
    (v) => v && v.title && v.title !== "Default Title" && v.title !== "Default"
  );
  if (variants.length < 2) return [];
  const titles = variants.map((v) => v.title);
  const allColors = titles.every((t) => !!COLOR_MAP[(t || "").toLowerCase().trim()]);
  return [
    {
      name: allColors ? (labels?.color || "Colore") : (labels?.version || "Versione"),
      values: titles,
      __synthetic: true,
    },
  ];
}

export default function ProductDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [tab, setTab] = useState("desc");
  const [chosen, setChosen] = useState({});
  const [related, setRelated] = useState([]);
  const { add } = useCart();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  // Prefer localized product name/description. DE → *_de fallback *_en fallback IT.
  const displayName = product
    ? (lang === "it"
        ? product.name
        : lang === "de"
          ? (product.name_de || product.name_en || product.name)
          : (product.name_en || product.name))
    : undefined;
  const displayDescription = product
    ? (lang === "it"
        ? product.description
        : lang === "de"
          ? (product.description_de || product.description_en || product.description)
          : (product.description_en || product.description))
    : undefined;
  const optionLabels = useMemo(() => ({
    color: t("pdp_option_group_color"),
    version: t("pdp_option_group_version"),
  }), [t]);
  const BADGE_LABEL = { sale: t("badge_sale"), new: t("badge_new"), best_seller: t("badge_best") };

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data));
    // Fetch admin-curated related products (accessories, cross-sells) — silent on failure.
    api.get(`/products/${id}/related`).then((r) => setRelated(r.data || [])).catch(() => setRelated([]));
  }, [id]);

  // Initialise chosen options — deep-link support:
  // If the URL has ?v=<variant_id> and that variant belongs to this product, preselect it.
  // Otherwise fall back to the first variant (previous default behaviour).
  useEffect(() => {
    if (!product) return;
    const variants = product.variants || [];
    const requestedId = searchParams.get("v");
    const requested = requestedId ? variants.find((v) => v.id === requestedId) : null;
    const target = requested || variants[0];
    const effOpts = computeEffectiveOptions(product, optionLabels);
    if (effOpts.length === 1 && effOpts[0].__synthetic) {
      if (target?.title) setChosen({ [effOpts[0].name]: target.title });
    } else if (target?.option_values) {
      setChosen(target.option_values);
    }
    // Meta Pixel + CAPI: ViewContent (fires once per product load).
    if (typeof window !== "undefined" && typeof window.audrivioTrack === "function") {
      window.audrivioTrack("ViewContent", {
        content_ids: [product.id],
        content_name: product.name,
        content_type: "product",
        content_category: product.brand || null,
        currency: "EUR",
        value: Number(target?.price ?? product.price ?? 0),
        variant_id: target?.id || null,
      });
    }
  }, [product, searchParams]);

  const effectiveOptions = useMemo(() => (product ? computeEffectiveOptions(product, optionLabels) : []), [product, optionLabels]);
  const activeVariant = useMemo(() => (product ? pickVariantByOptions(product, chosen, effectiveOptions) : null), [product, chosen, effectiveOptions]);
  const hasVariants = product && (product.variants || []).length > 1;
  const isRealVariant = hasVariants && activeVariant && activeVariant.title !== "Default Title";

  if (!product) {
    return (
      <div className="min-h-screen bg-xd-light">
        <Header />
        <div className="text-center py-20 text-xd-muted">{t("pdp_loading")}</div>
      </div>
    );
  }

  // Prefer the active variant image, otherwise product images
  const galleryImgs = product.images?.length ? product.images : [
    "https://images.unsplash.com/photo-1631035012469-751baca1d4a0?fm=jpg&q=85&w=940",
  ];
  const heroImage = (isRealVariant && activeVariant?.image) || galleryImgs[imgIdx];
  const currentPrice = activeVariant?.price ?? product.price;
  const currentCompare = activeVariant?.compare_at_price ?? product.compare_at_price;
  const waHref = `https://wa.me/393511294525?text=${encodeURIComponent(`${t("pdp_wa_hello")} ${displayName}${isRealVariant ? ` (${activeVariant.title})` : ""}`)}`;
  // Admin-edited description takes priority (rendered with pre-wrap to preserve spaces
  // and line breaks EXACTLY as typed). Legacy Shopify body_html is a fallback only.
  const hasEditedDescription = displayDescription && displayDescription.trim().length > 0;
  const cleanHtml = hasEditedDescription
    ? null
    : (product.body_html ? DOMPurify.sanitize(product.body_html, { USE_PROFILES: { html: true } }) : "");

  const handleAdd = () => add(product, 1, isRealVariant ? activeVariant : null);
  // Buy Now: adds to cart (still firing the deduped AddToCart Meta event) then
  // navigates directly to checkout, skipping the cart drawer for a faster funnel.
  // If the cart already has other items, they are preserved — we don't want to
  // silently drop products the user already committed to.
  const handleBuyNow = () => {
    add(product, 1, isRealVariant ? activeVariant : null, { openDrawer: false });
    navigate("/checkout");
  };
  const productPath = `/prodotto/${product.shopify_handle || product.id}`;
  const seoTitle = `${displayName} ${t("seo_pdp_title_suffix")} ${product.brand}`;
  const seoDesc = (displayDescription || "").slice(0, 155) || t("seo_pdp_desc_fallback");
  const breadcrumbLd = buildBreadcrumbLd([
    { name: t("nav_home"), path: "/" },
    { name: t("nav_catalog"), path: "/catalogo" },
    { name: product.brand, path: `/marca/${encodeURIComponent(product.brand.toLowerCase())}` },
    { name: displayName, path: productPath },
  ]);

  return (
    <div className="min-h-screen bg-xd-light">
      <SEO
        title={seoTitle}
        description={seoDesc}
        image={product.images?.[0]}
        path={productPath}
        lang={lang}
        type="product"
        product={product}
        jsonLd={[breadcrumbLd]}
        hreflangs={{ it: productPath, en: productPath, fr: productPath, de: productPath, es: productPath }}
      />
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/catalogo" className="text-xs text-xd-muted hover:text-xd-cyanHover inline-flex items-center gap-1 mb-4"><ChevronLeft className="w-3 h-3" /> {t("pdp_back_to_catalog")}</Link>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,480px)] gap-6 lg:gap-10">
          {/* Gallery */}
          <div className="min-w-0">
            <div className="bg-white border border-xd-border rounded-2xl aspect-[4/3] overflow-hidden w-full max-w-full">
              <img src={heroImage} alt={`${displayName} - foto principale`} loading="eager" fetchPriority="high" className="w-full h-full object-contain bg-white" data-testid="product-main-image" />
            </div>
            {galleryImgs.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {galleryImgs.map((im, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setImgIdx(i)}
                    data-testid={`gallery-thumb-${i}`}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 ${i === imgIdx && !(isRealVariant && activeVariant?.image) ? "border-xd-cyan" : "border-transparent hover:border-xd-cyan/40"}`}
                  >
                    <img src={im} alt={`${displayName} ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono-tech text-xs uppercase tracking-widest text-xd-muted">{product.brand} · {product.model}{product.years ? ` · ${product.years}` : ""}</span>
              {product.badge && (
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${product.badge === "sale" ? "badge-sale" : product.badge === "new" ? "badge-new" : "badge-best"}`}>
                  {BADGE_LABEL[product.badge]}
                </span>
              )}
            </div>
        <h1 data-testid="product-name" className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold mt-2 leading-tight break-words">{displayName}</h1>

            <div className="flex items-center gap-2 mt-3 text-sm">
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                ))}
              </div>
              <span className="font-semibold">{product.rating?.toFixed(1)}</span>
              <span className="text-xd-muted">({product.reviews_count} {t("pdp_reviews_suffix")})</span>
            </div>

            <div className="mt-6 flex items-end gap-3">
              <div className="font-display text-4xl font-semibold" data-testid="product-price">€ {currentPrice.toFixed(2)}</div>
              {currentCompare && currentCompare > currentPrice && (
                <div className="text-lg text-xd-muted line-through">€ {currentCompare.toFixed(2)}</div>
              )}
              {isRealVariant && activeVariant?.title && activeVariant.title !== "Default Title" && (
                <div className="text-xs text-xd-muted ml-auto self-center">
                  {t("pdp_variant_label")} <span className="font-mono-tech text-xd-cyanHover break-words whitespace-normal">{activeVariant.title}</span>
                </div>
              )}
            </div>

            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-xd-success" data-testid="pdp-shipping-badge">
              <Truck className="w-3.5 h-3.5" />
              <span>{lang === "it" ? t("ship_free_badge") : t("ship_eu_badge")}</span>
            </div>

            {/* Variant options */}
            {isRealVariant && effectiveOptions.map((opt) => (
              <VariantOption
                key={opt.name}
                option={opt}
                selected={chosen[opt.name]}
                currentPrice={currentPrice}
                onChange={(value) => setChosen((prev) => ({ ...prev, [opt.name]: value }))}
              />
            ))}

            {/* Klarna sempre disponibile — nessun limite lato audrivio. Eventuali soglie
                di approvazione (Pay in 3 / Pay Later) sono decise da Klarna in base al mercato. */}
            <div className="mt-5">
              <KlarnaBanner variant="inline" amount={currentPrice} />
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                data-testid="pdp-add-to-cart"
                onClick={handleAdd}
                disabled={activeVariant && activeVariant.available === false}
                className="rounded-lg px-6 py-3.5 text-sm font-semibold inline-flex items-center justify-center gap-2 flex-1 border-2 border-xd-orange text-xd-orange hover:bg-xd-orange hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ShoppingCart className="w-4 h-4" /> {activeVariant && activeVariant.available === false ? t("pdp_out_of_stock") : t("add_to_cart")}
              </button>
              <button
                type="button"
                data-testid="pdp-buy-now"
                onClick={handleBuyNow}
                disabled={activeVariant && activeVariant.available === false}
                className="btn-cta rounded-lg px-6 py-3.5 text-sm font-semibold inline-flex items-center justify-center gap-2 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Zap className="w-4 h-4" /> {t("pdp_buy_now")}
              </button>
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="pdp-whatsapp-btn" onClick={() => trackContact("whatsapp", "product_detail")} className="rounded-lg px-6 py-3.5 text-sm border-2 border-xd-success text-xd-success hover:bg-xd-success hover:text-white transition-colors inline-flex items-center justify-center gap-2">
                <MessageCircle className="w-4 h-4" /> {t("ask_whatsapp")}
              </a>
            </div>

            <div className="mt-3">
              <WishlistButton productId={product.id} variant="inline" />
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { icon: ShieldCheck, label: t("pdp_trust_warranty") },
                { icon: Truck, label: t("pdp_trust_shipping") },
                { icon: RefreshCcw, label: t("pdp_trust_return") },
              ].map((tr, i) => (
                <div key={i} className="bg-white border border-xd-border rounded-lg p-3 text-center">
                  <tr.icon className="w-4 h-4 mx-auto text-xd-cyanHover" />
                  <div className="text-[11px] font-medium mt-1">{tr.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs section */}
        <div className="mt-12 bg-white border border-xd-border rounded-2xl overflow-hidden">
          <div className="border-b border-xd-border flex gap-1 px-3 pt-2 flex-wrap">
            {[
              { key: "desc", label: t("pdp_tab_desc") },
              { key: "specs", label: t("pdp_tab_specs") },
              ...(isRealVariant ? [{ key: "versions", label: t("pdp_tab_versions") }] : []),
              { key: "trust", label: t("pdp_tab_trust") },
            ].map((tb) => (
              <button
                key={tb.key}
                type="button"
                onClick={() => setTab(tb.key)}
                data-testid={`pdp-tab-${tb.key}`}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === tb.key ? "border-xd-cyan text-xd-cyanHover" : "border-transparent text-xd-muted hover:text-xd-text"}`}
              >
                {tb.label}
              </button>
            ))}
          </div>

          {tab === "desc" && (
            hasEditedDescription ? (
              <div
                className="p-6 sm:p-8 max-w-3xl text-sm text-xd-text leading-relaxed whitespace-pre-wrap break-words"
                data-testid="product-description-plain"
              >{displayDescription}</div>
            ) : (
              <div
                className="p-6 sm:p-8 max-w-3xl xd-rich-desc"
                data-testid="product-description-html"
                dangerouslySetInnerHTML={{ __html: cleanHtml }}
              />
            )
          )}

          {tab === "specs" && (
            <div className="p-6 sm:p-8">
              <SpecsTable specs={product.specs || {}} t={t} />
            </div>
          )}

          {tab === "versions" && isRealVariant && (
            <div className="p-6 sm:p-8">
              <VersionsColumn
                product={product}
                effectiveOptions={effectiveOptions}
                selectedId={activeVariant?.id}
                t={t}
                onSelect={(v) => {
                  // update option chips to match the picked variant
                  const effOpts = effectiveOptions;
                  const next = {};
                  if (effOpts.length === 1 && effOpts[0].__synthetic) {
                    next[effOpts[0].name] = v.title;
                  } else {
                    effOpts.forEach((opt, i) => {
                      const key = opt.name;
                      const val = v.option_values?.[key] || v[`option${i + 1}`];
                      if (val) next[key] = val;
                    });
                  }
                  if (Object.keys(next).length) setChosen(next);
                  setTab("versions");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            </div>
          )}

          {tab === "trust" && (
            <div className="p-6 sm:p-8 grid sm:grid-cols-3 gap-4">
              {[
                { icon: ShieldCheck, title: t("pdp_trust_warranty"), desc: t("pdp_trust_warranty_desc") },
                { icon: Truck, title: t("pdp_trust_ship_title_long"), desc: t("pdp_trust_ship_desc") },
                { icon: RefreshCcw, title: t("pdp_trust_return_title_long"), desc: t("pdp_trust_return_desc") },
              ].map((c, i) => (
                <div key={i} className="border border-xd-border rounded-xl p-5">
                  <c.icon className="w-5 h-5 text-xd-cyanHover" />
                  <div className="font-display font-semibold text-xd-text mt-3">{c.title}</div>
                  <p className="text-sm text-xd-muted mt-1">{c.desc}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Related products (admin-curated cross-sells / accessories) */}
      {related.length > 0 && (
        <section className="bg-white border-t border-xd-border" data-testid="related-products-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-cyan">{t("pdp_related_kicker")}</div>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-1">{t("pdp_related_title")}</h2>
              </div>
              <a href="/accessori" className="text-sm text-xd-cyanHover hover:text-xd-cyan hidden sm:block">{t("pdp_related_link")}</a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

function VariantOption({ option, selected, onChange, currentPrice }) {
  const isColor = option.name.toLowerCase().includes("color");
  return (
    <div className="mt-6" data-testid={`variant-option-${option.name}`}>
      <div className="flex items-center justify-between mb-2 gap-3">
        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">{option.name}</div>
        {/* Inline price echo — critical on mobile where the main price at the top scrolls out
            of view. Users tap a chip below the fold and, on stock Android, cannot see the top
            price update. Showing it right next to the chips makes the feedback obvious. */}
        {typeof currentPrice === "number" && (
          <div className="font-mono-tech text-xs font-semibold text-xd-cyanHover tabular-nums" data-testid="variant-price-echo">€ {currentPrice.toFixed(2)}</div>
        )}
      </div>
      <div className="text-xs text-xd-cyanHover font-medium mb-2 line-clamp-2">{selected}</div>
      <div className={`flex ${isColor ? "gap-2" : "flex-wrap gap-2"}`}>
        {option.values.map((val) => {
          const active = selected === val;
          if (isColor) {
            const key = val.toLowerCase().trim();
            const color = COLOR_MAP[key] || "#94a3b8";
            return (
              <button
                key={val}
                type="button"
                onClick={() => onChange(val)}
                data-testid={`variant-swatch-${val}`}
                title={val}
                aria-label={val}
                style={{ background: color, touchAction: "manipulation" }}
                className={`relative w-9 h-9 rounded-full border-2 transition-all ${active ? "border-xd-cyan shadow-[0_0_0_3px_rgba(23,199,214,0.15)]" : "border-slate-200 hover:border-xd-cyan/40"}`}
              >
                {active && <Check className="w-4 h-4 text-white mix-blend-difference absolute inset-0 m-auto" />}
              </button>
            );
          }
          return (
            <button
              key={val}
              type="button"
              onClick={() => onChange(val)}
              data-testid={`variant-chip-${val.replace(/\s+/g, "-").slice(0, 30)}`}
              style={{ touchAction: "manipulation" }}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all min-w-[120px] text-left leading-tight ${active ? "border-xd-cyan bg-xd-cyan/5 text-xd-cyanHover" : "border-xd-border text-xd-text hover:border-xd-cyan/40 bg-white"}`}
            >
              {val}
            </button>
          );
        })}
      </div>
    </div>
  );
}


/**
 * Structured specs table: groups core hardware specs first, then flattens
 * the "Funzioni incluse" comma-list into pill badges.
 */
function SpecsTable({ specs, t }) {
  const CORE_ORDER = [
    "Marchio", "Modello", "Anni", "Sistema operativo", "Schermo",
    "RAM/ROM", "Ram", "Rom", "Cpu", "CPU", "Installazione", "Categoria", "Colore",
  ];
  const entries = Object.entries(specs || {});
  const funzioni = specs["Funzioni incluse"] || "";
  const features = funzioni.split(",").map((s) => s.trim()).filter(Boolean);
  const core = entries.filter(([k]) => k !== "Funzioni incluse");
  core.sort((a, b) => {
    const ia = CORE_ORDER.indexOf(a[0]);
    const ib = CORE_ORDER.indexOf(b[0]);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });

  return (
    <div>
      <table className="w-full text-sm" data-testid="specs-table">
        <tbody>
          {core.map(([k, v]) => (
            <tr key={k} className="border-b border-xd-border last:border-b-0">
              <td className="px-2 py-3 font-mono-tech text-[11px] uppercase tracking-widest text-xd-muted w-1/3">{k}</td>
              <td className="px-2 py-3 text-xd-text">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {features.length > 0 && (
        <div className="mt-8" data-testid="specs-features">
          <div className="font-mono-tech text-[11px] uppercase tracking-widest text-xd-muted mb-3">
            {t ? t("pdp_features_included") : "Funzioni incluse"} ({features.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {features.map((f) => (
              <span key={f} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-xd-cyan/10 text-xd-cyanHover border border-xd-cyan/30">
                <Check className="w-3 h-3" /> {f}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Column-based variant listing. Each variant is a full-width row showing
 * title, options, price + discount, and a Select button. Designed for a
 * "comparison" feel similar to Apple's iPhone models table.
 */
function VersionsColumn({ product, effectiveOptions, selectedId, onSelect, t }) {
  // Only show option pill badges when we have real (non-synthetic) options that
  // actually map to variants. Otherwise the variant title alone is enough.
  const opts = (effectiveOptions && effectiveOptions[0]?.__synthetic) ? [] : (effectiveOptions || product.options || []);
  const variants = (product.variants || []).filter((v) => v.price > 0);
  if (!variants.length) return <div className="text-sm text-xd-muted">{t ? t("pdp_versions_none") : "Nessuna variante disponibile."}</div>;

  return (
    <div className="space-y-3" data-testid="versions-column">
      {variants.map((v) => {
        const isActive = v.id === selectedId;
        const optionValues = opts
          .map((opt, i) => v.option_values?.[opt.name] || v[`option${i + 1}`])
          .filter(Boolean);
        const compare = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : null;
        const off = compare ? Math.round(((compare - v.price) / compare) * 100) : 0;
        return (
          <div
            key={v.id}
            data-testid={`version-row-${v.id}`}
            className={`grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 rounded-xl border p-4 transition-colors ${isActive ? "border-xd-cyan bg-xd-cyan/5" : "border-xd-border bg-white hover:border-xd-cyan/40"}`}
          >
            <div className="min-w-0">
              <div className="font-display font-semibold text-xd-text leading-tight break-words whitespace-normal">
                {v.title && v.title !== "Default Title" ? v.title : (t ? t("pdp_version_base") : "Versione base")}
              </div>
              {optionValues.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {optionValues.map((val, i) => (
                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono-tech uppercase tracking-widest text-xd-muted bg-slate-100">{val}</span>
                  ))}
                </div>
              )}
              {v.sku && <div className="mt-2 text-[11px] font-mono-tech text-xd-muted">SKU · {v.sku}</div>}
            </div>

            <div className="text-right md:pr-4">
              {compare && <div className="text-xs text-xd-muted line-through">€ {Number(compare).toFixed(2)}</div>}
              <div className="font-display text-xl font-semibold">€ {Number(v.price).toFixed(2)}</div>
              {off > 0 && <div className="text-[10px] font-semibold text-xd-orange">-{off}%</div>}
            </div>

            <div>
              <button
                type="button"
                onClick={() => onSelect(v)}
                data-testid={`version-select-${v.id}`}
                disabled={isActive}
                className={`h-10 px-4 rounded-lg text-xs font-semibold transition-colors ${isActive ? "bg-xd-cyan/10 text-xd-cyanHover cursor-default border border-xd-cyan/30" : "bg-xd-orange text-white hover:opacity-90"}`}
              >
                {isActive ? (t ? t("pdp_version_selected") : "Selezionata") : (t ? t("pdp_version_select") : "Seleziona")}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
